import { HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { HttpClientService } from '../../shared/services/http-client/http-client.service';
import { QuestionarioListQueryParams } from '../api/models/questionario-list-query-params';
import { QuestionarioResponseDto } from '../api/models/questionario-response.dto';
import { CriarQuestionarioRequest } from '../models/questionario.model';
import { CreateQuestionarioCommandDto } from '../api/models/create-questionario-command.dto';
import { UpdateQuestionarioCommandDto } from '../api/models/update-questionario-command.dto';
import { PatchQuestionarioCommandDto } from '../api/models/patch-questionario-command.dto';
import { QuestionarioPerguntaResponseDto } from '../api/models/questionario-pergunta-response.dto';
import { QuestionarioAlternativaResponseDto } from '../api/models/questionario-alternativa-response.dto';
import { CreateQuestionarioPerguntaCommandDto } from '../api/models/create-questionario-pergunta-command.dto';
import { UpdateQuestionarioPerguntaCommandDto } from '../api/models/update-questionario-pergunta-command.dto';
import { CreateQuestionarioAlternativaCommandDto } from '../api/models/create-questionario-alternativa-command.dto';
import { UpdateQuestionarioAlternativaCommandDto } from '../api/models/update-questionario-alternativa-command.dto';
import { PerfilInvestimentoPontuacaoResponseDto } from '../api/models/perfil-investimento-pontuacao-response.dto';
import { CreatePerfilInvestimentoPontuacaoCommandDto } from '../api/models/create-perfil-investimento-pontuacao-command.dto';
import {
  PerfilInvestimentoPontuacaoValores,
  UpdatePerfilInvestimentoPontuacaoCommandDto,
} from '../api/models/update-perfil-investimento-pontuacao-command.dto';

const QUESTIONARIO_BASE_PATH = '/v1/Questionario';
const PERGUNTA_BASE_PATH = '/v1/QuestionarioPergunta';
const ALTERNATIVA_BASE_PATH = '/v1/QuestionarioAlternativa';
const PERFIL_INVESTIMENTO_BASE_PATH = '/v1/PerfilInvestimento';

@Injectable({
  providedIn: 'root',
})
export class QuestionarioApiService {
  constructor(private readonly httpClient: HttpClientService) {}

  listar(params?: QuestionarioListQueryParams): Observable<QuestionarioResponseDto[]> {
    return this.httpClient.get<QuestionarioResponseDto[]>(QUESTIONARIO_BASE_PATH, {
      params: this.toHttpParams(params),
    });
  }

  obter(id: number | string): Observable<QuestionarioResponseDto> {
    return this.httpClient.get<QuestionarioResponseDto>(`${QUESTIONARIO_BASE_PATH}/${id}`);
  }

  criar(command: CreateQuestionarioCommandDto): Observable<QuestionarioResponseDto> {
    return this.httpClient.post<QuestionarioResponseDto>(QUESTIONARIO_BASE_PATH, command);
  }

  /**
   * Criação completa: perguntas, alternativas e pontuações vão no mesmo corpo, então uma
   * criação inteira custa uma única requisição.
   */
  criarCompleto(request: CriarQuestionarioRequest): Observable<QuestionarioResponseDto> {
    return this.httpClient.post<QuestionarioResponseDto>(QUESTIONARIO_BASE_PATH, request);
  }

  atualizar(
    id: number | string,
    command: UpdateQuestionarioCommandDto,
  ): Observable<QuestionarioResponseDto> {
    return this.httpClient.put<QuestionarioResponseDto>(`${QUESTIONARIO_BASE_PATH}/${id}`, command);
  }

  patch(
    id: number | string,
    command: PatchQuestionarioCommandDto,
  ): Observable<QuestionarioResponseDto> {
    return this.httpClient.patch<QuestionarioResponseDto>(
      `${QUESTIONARIO_BASE_PATH}/${id}`,
      command,
    );
  }

  excluir(id: number | string): Observable<void> {
    return this.httpClient.delete<void>(`${QUESTIONARIO_BASE_PATH}/${id}`);
  }

  listarPerguntas(idQuestionario: number | string): Observable<QuestionarioPerguntaResponseDto[]> {
    return this.httpClient.get<QuestionarioPerguntaResponseDto[]>(
      `${QUESTIONARIO_BASE_PATH}/${idQuestionario}/Perguntas`,
    );
  }

  listarAlternativas(
    idQuestionario: number | string,
  ): Observable<QuestionarioAlternativaResponseDto[]> {
    return this.httpClient.get<QuestionarioAlternativaResponseDto[]>(
      `${QUESTIONARIO_BASE_PATH}/${idQuestionario}/Alternativas`,
    );
  }

  criarPergunta(
    command: CreateQuestionarioPerguntaCommandDto,
  ): Observable<QuestionarioPerguntaResponseDto> {
    return this.httpClient.post<QuestionarioPerguntaResponseDto>(PERGUNTA_BASE_PATH, command);
  }

  atualizarPergunta(
    id: number | string,
    command: UpdateQuestionarioPerguntaCommandDto,
  ): Observable<QuestionarioPerguntaResponseDto> {
    return this.httpClient.put<QuestionarioPerguntaResponseDto>(
      `${PERGUNTA_BASE_PATH}/${id}`,
      command,
    );
  }

  excluirPergunta(id: number | string): Observable<void> {
    return this.httpClient.delete<void>(`${PERGUNTA_BASE_PATH}/${id}`);
  }

  criarAlternativa(
    command: CreateQuestionarioAlternativaCommandDto,
  ): Observable<QuestionarioAlternativaResponseDto> {
    return this.httpClient.post<QuestionarioAlternativaResponseDto>(ALTERNATIVA_BASE_PATH, command);
  }

  atualizarAlternativa(
    id: number | string,
    command: UpdateQuestionarioAlternativaCommandDto,
  ): Observable<QuestionarioAlternativaResponseDto> {
    return this.httpClient.put<QuestionarioAlternativaResponseDto>(
      `${ALTERNATIVA_BASE_PATH}/${id}`,
      command,
    );
  }

  excluirAlternativa(id: number | string): Observable<void> {
    return this.httpClient.delete<void>(`${ALTERNATIVA_BASE_PATH}/${id}`);
  }

  /**
   * Lista perguntas de TODOS os questionários (sem `idQuestionario`), usada pelo
   * modal "Selecionar perguntas já cadastradas" para reaproveitar perguntas.
   */
  listarTodasPerguntas(filtros?: {
    texto?: string;
    tipo?: string;
  }): Observable<QuestionarioPerguntaResponseDto[]> {
    let params = new HttpParams();
    if (filtros?.texto) {
      params = params.set('texto', filtros.texto);
    }
    if (filtros?.tipo) {
      params = params.set('tipo', filtros.tipo);
    }

    return this.httpClient.get<QuestionarioPerguntaResponseDto[]>(PERGUNTA_BASE_PATH, {
      params: params.keys().length > 0 ? params : undefined,
    });
  }

  listarAlternativasPorPergunta(
    idPergunta: number | string,
  ): Observable<QuestionarioAlternativaResponseDto[]> {
    return this.httpClient.get<QuestionarioAlternativaResponseDto[]>(ALTERNATIVA_BASE_PATH, {
      params: new HttpParams().set('idPergunta', String(idPergunta)),
    });
  }

  listarPontuacoes(
    idQuestionario: number | string,
  ): Observable<PerfilInvestimentoPontuacaoResponseDto[]> {
    return this.httpClient.get<PerfilInvestimentoPontuacaoResponseDto[]>(
      `${PERFIL_INVESTIMENTO_BASE_PATH}/Pontuacao`,
      { params: new HttpParams().set('idQuestionario', String(idQuestionario)) },
    );
  }

  criarPontuacao(
    idPerfilInvestimento: number,
    command: CreatePerfilInvestimentoPontuacaoCommandDto,
  ): Observable<PerfilInvestimentoPontuacaoResponseDto> {
    return this.httpClient.post<PerfilInvestimentoPontuacaoResponseDto>(
      `${PERFIL_INVESTIMENTO_BASE_PATH}/${idPerfilInvestimento}/Pontuacao`,
      command,
    );
  }

  /** O endpoint recebe os dois ids na URL e os repete no corpo, junto das pontuações. */
  atualizarPontuacao(
    idPerfilInvestimento: number,
    idQuestionario: number,
    pontuacoes: PerfilInvestimentoPontuacaoValores,
  ): Observable<PerfilInvestimentoPontuacaoResponseDto> {
    const command: UpdatePerfilInvestimentoPontuacaoCommandDto = {
      idPerfilInvestimento,
      idQuestionario,
      ...pontuacoes,
    };

    return this.httpClient.put<PerfilInvestimentoPontuacaoResponseDto>(
      `${PERFIL_INVESTIMENTO_BASE_PATH}/${idPerfilInvestimento}/Pontuacao/${idQuestionario}`,
      command,
    );
  }

  excluirPontuacao(idPerfilInvestimento: number, idQuestionario: number): Observable<void> {
    return this.httpClient.delete<void>(
      `${PERFIL_INVESTIMENTO_BASE_PATH}/${idPerfilInvestimento}/Pontuacao/${idQuestionario}`,
    );
  }

  private toHttpParams(params?: QuestionarioListQueryParams): HttpParams | undefined {
    if (!params) {
      return undefined;
    }

    let httpParams = new HttpParams();

    if (params.idQuestionario != null) {
      httpParams = httpParams.set('idQuestionario', String(params.idQuestionario));
    }

    if (params.nome) {
      httpParams = httpParams.set('nome', params.nome);
    }

    if (params.status) {
      httpParams = httpParams.set('status', params.status);
    }

    return httpParams.keys().length > 0 ? httpParams : undefined;
  }
}
